import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { Dayjs } from "dayjs";
import type { FC } from "react";
import { useEffect, useState } from "react";
import AsyncLoadingButton from "../../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../../components/cancelButton/CancelButton";
import DatePickerV2 from "../../../components/datePicker/DatePickerV2";
import dayjs from "../../../dayjs";

export interface CancelProductDialogItem {
  readonly index: number;
  readonly name: string;
  readonly date: string;
}

interface Props {
  readonly open: boolean;
  readonly products: CancelProductDialogItem[];
  readonly onConfirm: (selectedIndexes: number[], cancelledAt: string) => Promise<void>;
  readonly onCancel: () => void;
}

const CancelProductDialog: FC<Props> = ({ open, products, onConfirm, onCancel }) => {
  const [cancelledAt, setCancelledAt] = useState<Dayjs>(dayjs());
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open && products.length > 0) {
      setSelectedIndexes(new Set(products.map((p) => p.index)));
    }
  }, [open, products]);

  const handleToggle = (index: number) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedIndexes(checked ? new Set(products.map((p) => p.index)) : new Set());
  };

  const handleConfirm = () =>
    onConfirm(
      [...selectedIndexes].sort((a, b) => a - b),
      cancelledAt.toISOString(),
    );

  const allSelected = products.length > 0 && selectedIndexes.size === products.length;
  const someSelected = selectedIndexes.size > 0;

  return (
    <Dialog open={open} maxWidth="lg">
      <DialogTitle>Anuluj produkty</DialogTitle>
      <DialogContent>
        <Stack
          spacing={2}
          sx={{
            marginTop: 1,
          }}
        >
          <Typography>
            Czy na pewno chcesz anulować ten produkt? Jeśli tak, wybierz produkty z tej samej rezerwacji, które mają
            zostać anulowane:
          </Typography>
          {products.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={(_, checked) => handleSelectAll(checked)}
                      aria-label="Zaznacz wszystkie"
                    />
                  </TableCell>
                  <TableCell>Produkt</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map(({ index, name, date }) => (
                  <TableRow key={index}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIndexes.has(index)}
                        onChange={() => handleToggle(index)}
                        aria-label={`Zaznacz ${name}`}
                      />
                    </TableCell>
                    <TableCell>{name}</TableCell>
                    <TableCell>{date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <DatePickerV2
            label="Data anulacji"
            value={cancelledAt}
            onChange={(date) => setCancelledAt(date ?? dayjs())}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onCancel} />
        <AsyncLoadingButton onClick={handleConfirm} disabled={!someSelected}>
          Potwierdź anulację
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CancelProductDialog;
